import React, { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import Panel from "../../../components/ui/Panel/Panel.jsx";
import Button from "../../../components/ui/Button/Button.jsx";
import Checkbox from "../../../components/forms/Checkbox/Checkbox.jsx";
import FormField from "../../../components/forms/FormField/FormField.jsx";
import TextInput from "../../../components/forms/TextInput/TextInput.jsx";
import ConfirmModal from "../../../components/ui/Modal/ConfirmModal.jsx";
import TriStateCheckbox from "../../../components/ui/TriStateCheckbox/TriStateCheckbox.jsx";
import BulkDeleteBar from "../../../components/ui/BulkDeleteBar/BulkDeleteBar.jsx";
import { useRowSelection } from "../../../hooks/useRowSelection.js";
import { getPriorYearOptions } from "../../../services/lookupService.js";
import { getPriorYear, savePriorYear } from "../../../services/priorYearService.js";
import { getToken } from "../../../services/api.js";
import { useApp } from "../../../hooks/useApp.js";
import { isValidVin, sanitizeVinInput, validateBuyerTransfereeName, FIELD_FORMATS } from "../../../utils/validators.js";
import { PRIOR_PERIOD_END, PRIOR_PERIOD_MIN_DATE, PRIOR_PERIOD_HINT } from "../../../utils/priorPeriod.js";

// Matches the current backend seed data (lookup_prior_year_options). Kept as
// the initial state so the step still renders immediately if the lookup
// fetch is slow or fails.
const DEFAULT_PRIOR_YEAR_OPTIONS = [
  {
    id: "line8a",
    title: "They stayed under the mileage limit",
    subtitle:
      "Confirm last year's suspended vehicles didn't exceed 5,000 miles (7,500 agricultural), except any listed below. (Line 8a)",
  },
  {
    id: "line9",
    title: "I sold or transferred a suspended vehicle",
    subtitle: "A vehicle suspended in a prior period was sold or transferred. (Line 9)",
  },
];

let nextRowId = 1;

// Part II, Lines 8a/8b/9 - own screen so the VIN lists can grow without
// cramping the Trucks step. Same add-form-then-table pattern as TrucksStep:
// a row is only added once its draft fields validate, then it's read-only
// in the table below (add/remove only, no in-place edit).
const PriorYearStep = ({ onNext, onBack }) => {
  const { state, showToast } = useApp();
  const usingBackend = !!(getToken() && state.returnId);

  const [line8a, setLine8a] = useState(false);
  const [line8bRows, setLine8bRows] = useState([]);
  const [line9, setLine9] = useState(false);
  const [line9Rows, setLine9Rows] = useState([]);
  const [priorYearOptions, setPriorYearOptions] = useState(DEFAULT_PRIOR_YEAR_OPTIONS);
  const [priorYearOptionsLoading, setPriorYearOptionsLoading] = useState(true);
  const [loadingSaved, setLoadingSaved] = useState(usingBackend);
  const [saving, setSaving] = useState(false);

  // Set on Continue/Skip when a checkbox above is checked but its table is
  // still empty - that combination saves as "checked, nothing listed", which
  // the IRS has rejected as inconsistent. Cleared as soon as a row is added.
  const [line8bTableError, setLine8bTableError] = useState(false);
  const [line9TableError, setLine9TableError] = useState(false);

  const [draftLine8bVin, setDraftLine8bVin] = useState("");
  const [line8bError, setLine8bError] = useState("");
  const [editingLine8bId, setEditingLine8bId] = useState(null);

  const [draftLine9, setDraftLine9] = useState({ vin: "", buyer: "", date: "" });
  const [line9Errors, setLine9Errors] = useState({});
  const [editingLine9Id, setEditingLine9Id] = useState(null);

  // No per-row backend endpoint for either table (both are only persisted as
  // a full replace via savePriorYear on Continue/Skip - see handleContinue),
  // so like WeightIncreaseStep's confirm, this is just local state - confirm,
  // then remove immediately.
  const [pendingLine8bRemoveId, setPendingLine8bRemoveId] = useState(null);
  const [pendingLine9RemoveId, setPendingLine9RemoveId] = useState(null);

  // Bulk-select for both tables, same useRowSelection hook TrucksStep/
  // VinCorrectionStep/etc. share - keyed by row.id (local nextRowId, never a
  // real backend id, since both tables are local-only until Continue/Skip).
  const line8bSelection = useRowSelection();
  const line9Selection = useRowSelection();
  const [bulk8bConfirmOpen, setBulk8bConfirmOpen] = useState(false);
  const [bulk9ConfirmOpen, setBulk9ConfirmOpen] = useState(false);

  const findOption = (id) => priorYearOptions.find((o) => o.id === id);

  const startEditLine8b = (row) => {
    setDraftLine8bVin(row.vin);
    setLine8bError("");
    setEditingLine8bId(row.id);
  };

  const addLine8bRow = () => {
    const v = draftLine8bVin.trim().toUpperCase();
    if (!isValidVin(v)) {
      setLine8bError("Enter a valid VIN.");
      return;
    }
    if (line8bRows.some((r) => r.vin === v && r.id !== editingLine8bId)) {
      setLine8bError("That VIN is already on this list.");
      return;
    }
    if (line9Rows.some((r) => r.vin === v)) {
      setLine8bError("That VIN is already listed as sold or transferred (Line 9).");
      return;
    }
    if (editingLine8bId != null) {
      setLine8bRows((rows) => rows.map((r) => (r.id === editingLine8bId ? { ...r, vin: v } : r)));
    } else {
      setLine8bRows((rows) => [...rows, { id: nextRowId++, vin: v }]);
    }
    setDraftLine8bVin("");
    setLine8bError("");
    setEditingLine8bId(null);
    setLine8bTableError(false);
  };

  const cancelLine8bEdit = () => {
    setDraftLine8bVin("");
    setLine8bError("");
    setEditingLine8bId(null);
  };

  const startEditLine9 = (row) => {
    setDraftLine9({ vin: row.vin, buyer: row.buyer, date: row.date });
    setLine9Errors({});
    setEditingLine9Id(row.id);
  };

  const addLine9Row = () => {
    const v = draftLine9.vin.trim().toUpperCase();
    const buyer = draftLine9.buyer.trim();
    const errs = {};
    if (!isValidVin(v)) errs.vin = "Enter a valid VIN.";
    else if (line9Rows.some((r) => r.vin === v && r.id !== editingLine9Id)) {
      errs.vin = "That VIN is already on this list.";
    } else if (line8bRows.some((r) => r.vin === v)) {
      errs.vin = "That VIN is already listed under the mileage limit (Line 8b).";
    }
    const buyerErr = validateBuyerTransfereeName(draftLine9.buyer);
    if (buyerErr) errs.buyer = buyerErr;
    if (!draftLine9.date) errs.date = "Date is required.";
    setLine9Errors(errs);
    if (Object.keys(errs).length) return;

    if (editingLine9Id != null) {
      setLine9Rows((rows) => rows.map((r) => (r.id === editingLine9Id ? { ...r, vin: v, buyer, date: draftLine9.date } : r)));
    } else {
      setLine9Rows((rows) => [...rows, { id: nextRowId++, vin: v, buyer, date: draftLine9.date }]);
    }
    setDraftLine9({ vin: "", buyer: "", date: "" });
    setLine9Errors({});
    setEditingLine9Id(null);
    setLine9TableError(false);
  };

  const cancelLine9Edit = () => {
    setDraftLine9({ vin: "", buyer: "", date: "" });
    setLine9Errors({});
    setEditingLine9Id(null);
  };

  const removeRow = (setRows, id) => setRows((rows) => rows.filter((r) => r.id !== id));

  const confirmRemoveLine8b = () => {
    const id = pendingLine8bRemoveId;
    removeRow(setLine8bRows, id);
    if (editingLine8bId === id) cancelLine8bEdit();
    setPendingLine8bRemoveId(null);
  };

  const confirmRemoveLine9 = () => {
    const id = pendingLine9RemoveId;
    removeRow(setLine9Rows, id);
    if (editingLine9Id === id) cancelLine9Edit();
    setPendingLine9RemoveId(null);
  };

  // Bulk delete for both tables - synchronous local filtering (no backend
  // call, see the note on pendingLine8bRemoveId/pendingLine9RemoveId above),
  // but still confirms first like TrucksStep's bulk delete does: removing
  // more than one row at once gets the same guardrail regardless of whether
  // it's backed by an API call.
  const confirmBulk8bDelete = () => {
    setLine8bRows((rows) => rows.filter((r) => !line8bSelection.selectedIds.has(r.id)));
    if (editingLine8bId != null && line8bSelection.selectedIds.has(editingLine8bId)) cancelLine8bEdit();
    line8bSelection.clear();
    setBulk8bConfirmOpen(false);
  };

  const confirmBulk9Delete = () => {
    setLine9Rows((rows) => rows.filter((r) => !line9Selection.selectedIds.has(r.id)));
    if (editingLine9Id != null && line9Selection.selectedIds.has(editingLine9Id)) cancelLine9Edit();
    line9Selection.clear();
    setBulk9ConfirmOpen(false);
  };

  // Reloads previously saved prior-year data when the step is (re)entered
  // with a backend return - without this, revisiting Prior Year after
  // leaving the wizard would show an empty form even though it was saved.
  useEffect(() => {
    if (!usingBackend) {
      setLoadingSaved(false);
      return;
    }
    let cancelled = false;
    getPriorYear(state.returnId)
      .then((saved) => {
        if (cancelled) return;
        setLine8a(saved.line8aChecked);
        setLine8bRows(saved.line8bVins.map((vin) => ({ id: nextRowId++, vin })));
        setLine9(saved.line9Checked);
        setLine9Rows(
          saved.line9Transfers.map((t) => ({ id: nextRowId++, vin: t.vin, buyer: t.buyerName, date: t.transferDate }))
        );
      })
      .catch(() => {
        /* no saved prior-year data yet -> keep the blank form */
      })
      .finally(() => {
        if (!cancelled) setLoadingSaved(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.returnId]);

  // PUT /returns/{returnId}/prior-year is a full replace, so both "Skip"
  // and "Continue" push whatever's currently on screen (including an empty
  // form, which clears any previously saved rows) before moving on. A
  // checkbox left checked with its table still empty saves as "checked, but
  // nothing listed" - inconsistent data the IRS has rejected - so block both
  // buttons on that combination instead of only validating the add-row form.
  const handleContinue = async () => {
    const bad8b = line8a && line8bRows.length === 0;
    const bad9 = line9 && line9Rows.length === 0;
    setLine8bTableError(bad8b);
    setLine9TableError(bad9);
    if (bad8b || bad9) {
      showToast("Add at least one vehicle to the checked list below, or uncheck it, before continuing.");
      return;
    }

    if (usingBackend) {
      setSaving(true);
      try {
        await savePriorYear(state.returnId, {
          line8aChecked: line8a,
          line8bVins: line8bRows.map((r) => r.vin),
          line9Checked: line9,
          line9Transfers: line9Rows.map((r) => ({ vin: r.vin, buyerName: r.buyer, transferDate: r.date })),
        });
      } catch (err) {
        setSaving(false);
        showToast(err.message || "Couldn't save. Please try again.");
        return;
      }
      setSaving(false);
    }
    onNext();
  };

  useEffect(() => {
    let cancelled = false;
    getPriorYearOptions()
      .then((options) => {
        if (cancelled) return;
        if (Array.isArray(options) && options.length) {
          setPriorYearOptions(options.map((o) => ({ id: o.code, title: o.title, subtitle: o.subtitle })));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setPriorYearOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Panel padded>
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
         Prior year
      </div>
      <h2 className="text-2xl m-[4px_0]">Prior-year suspended vehicles</h2>
      <p className="text-[var(--muted)] max-w-[62ch] mb-4">
        Optional — only if you reported suspended (category W) vehicles on <b>last year's</b> Form 2290. If not, just
        continue.
      </p>

      <Checkbox
        id="p2Line8a"
        title={findOption("line8a").title}
        subtitle={findOption("line8a").subtitle}
        checked={line8a}
        onChange={(e) => {
          setLine8a(e.target.checked);
          if (!e.target.checked) setLine8bTableError(false);
        }}
        disabled={priorYearOptionsLoading}
      />
      {line8bTableError && (
        <div className="text-xs text-[var(--danger)] mt-1.5 ml-1">
          Add at least one VIN below, or uncheck this box.
        </div>
      )}
      {line8a && (
        <div className="ml-1 mt-2 mb-1">
          <div className="text-[13px] text-[var(--muted)] mb-1.5">
            VINs of prior-year suspended vehicles that <b>did</b> exceed the limit (Line 8b):
          </div>

          <div className="flex gap-2 items-start mb-2">
            <FormField label="VIN" required error={line8bError} showError={!!line8bError} style={{ flex: 1, marginBottom: 0 }}>
              <TextInput
                mono
                placeholder="VIN that exceeded the limit"
                maxLength={19}
                value={draftLine8bVin}
                onChange={(e) => setDraftLine8bVin(sanitizeVinInput(e.target.value))}
                invalid={!!line8bError}
              />
            </FormField>
            <Button variant="ghost" onClick={addLine8bRow} className="mt-6">
              {editingLine8bId != null ? "Update" : "+ Add"}
            </Button>
            {editingLine8bId != null && (
              <Button variant="ghost" onClick={cancelLine8bEdit} className="mt-6">
                Cancel
              </Button>
            )}
          </div>

          {line8bRows.length > 0 && (
            <BulkDeleteBar
              count={line8bSelection.count}
              onDelete={() => setBulk8bConfirmOpen(true)}
            />
          )}

          <table className="w-full border-collapse mb-1">
            <thead>
              <tr>
                {line8bRows.length > 0 && (
                  <th className="text-left p-3 border-b border-[var(--line)] w-[36px]">
                    <TriStateCheckbox
                      checked={line8bRows.length > 0 && line8bSelection.count === line8bRows.length}
                      indeterminate={line8bSelection.count > 0 && line8bSelection.count < line8bRows.length}
                      onChange={() =>
                        line8bSelection.selectAll(
                          line8bSelection.count === line8bRows.length ? [] : line8bRows.map((r) => r.id)
                        )
                      }
                      label="Select all prior-year suspended vehicles"
                    />
                  </th>
                )}
                {["#", "VIN", "Actions"].map((h, hi, arr) => (
                  <th
                    key={h}
                    className={[
                      "p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold",
                      hi === arr.length - 1 ? "text-right" : "text-left",
                    ].join(" ")}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {line8bRows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-[var(--muted)] p-[16px_12px] text-sm">
                    None — everything stayed under the limit.
                  </td>
                </tr>
              ) : (
                line8bRows.map((row, i) => (
                  <tr key={row.id} className="hover:bg-[var(--accent-soft)] transition-colors">
                    <td className="text-left p-3 border-b border-[var(--line)]">
                      <TriStateCheckbox
                        checked={line8bSelection.selectedIds.has(row.id)}
                        onChange={() => line8bSelection.toggle(row.id)}
                        label={`Select row ${i + 1}`}
                      />
                    </td>
                    <td className="text-left p-3 border-b border-[var(--line)]">{i + 1}</td>
                    <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">{row.vin}</td>
                    <td className="text-right p-3 border-b border-[var(--line)] whitespace-nowrap">
                      <button
                        type="button"
                        title="Edit"
                        onClick={() => startEditLine8b(row)}
                        className="inline-flex items-center justify-center border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 hover:text-[var(--accent)]"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        title="Delete"
                        onClick={() => setPendingLine8bRemoveId(row.id)}
                        className="inline-flex items-center justify-center border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 ml-1 hover:text-[var(--danger)]"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <ConfirmModal
            open={pendingLine8bRemoveId != null}
            title="Delete Confirmation"
            confirmLabel="Delete"
            onConfirm={confirmRemoveLine8b}
            onCancel={() => setPendingLine8bRemoveId(null)}
          >
            Are you sure you want to delete this record? This action cannot be undone.
          </ConfirmModal>

          <ConfirmModal
            open={bulk8bConfirmOpen}
            title="Delete Selected Records?"
            confirmLabel="Delete"
            onConfirm={confirmBulk8bDelete}
            onCancel={() => setBulk8bConfirmOpen(false)}
          >
            Are you sure you want to delete these {line8bSelection.count} record{line8bSelection.count === 1 ? "" : "s"}?
            This action cannot be undone.
          </ConfirmModal>
        </div>
      )}

      <div className="mt-3">
        <Checkbox
          id="p2Line9chk"
          title={findOption("line9").title}
          subtitle={findOption("line9").subtitle}
          checked={line9}
          onChange={(e) => {
            setLine9(e.target.checked);
            if (!e.target.checked) setLine9TableError(false);
          }}
          disabled={priorYearOptionsLoading}
        />
        {line9TableError && (
          <div className="text-xs text-[var(--danger)] mt-1.5">
            Add at least one vehicle below, or uncheck this box.
          </div>
        )}
      </div>
      {line9 && (
        <div className="ml-1 mt-2 mb-1">
          <p className="text-[13px] text-[var(--muted)] mb-1.5">Add each suspended vehicle you sold or transferred.</p>

          <Panel tone="soft" className="!shadow-none p-[16px] mb-3">
            <FormField label="VIN" required error={line9Errors.vin} showError={!!line9Errors.vin}>
              <TextInput
                mono
                maxLength={19}
                placeholder="Suspended vehicle sold/transferred"
                value={draftLine9.vin}
                onChange={(e) => setDraftLine9((d) => ({ ...d, vin: sanitizeVinInput(e.target.value) }))}
                invalid={!!line9Errors.vin}
              />
            </FormField>
            <div className="grid gap-3.5 sm:grid-cols-2">
              <FormField
                label="Buyer / transferee name"
                required
                error={line9Errors.buyer}
                showError={!!line9Errors.buyer}
                hint={FIELD_FORMATS.buyerTransferee}
              >
                <TextInput
                  placeholder="Who you sold or transferred it to"
                  maxLength={75}
                  value={draftLine9.buyer}
                  onChange={(e) => setDraftLine9((d) => ({ ...d, buyer: e.target.value }))}
                  invalid={!!line9Errors.buyer}
                />
              </FormField>
              <FormField label="Date sold/transferred" required error={line9Errors.date} showError={!!line9Errors.date} hint={PRIOR_PERIOD_HINT}>
                <TextInput
                  type="date"
                  min={PRIOR_PERIOD_MIN_DATE}
                  max={PRIOR_PERIOD_END}
                  value={draftLine9.date}
                  onChange={(e) => {
                    // Clamp outright rather than let an out-of-range date (native
                    // date-picker selection or manual typing) get saved - same
                    // guard CreditsStep applies to its prior-period date field.
                    let v = e.target.value;
                    if (v && v > PRIOR_PERIOD_END) v = PRIOR_PERIOD_END;
                    else if (v && v < PRIOR_PERIOD_MIN_DATE) v = PRIOR_PERIOD_MIN_DATE;
                    setDraftLine9((d) => ({ ...d, date: v }));
                  }}
                  invalid={!!line9Errors.date}
                />
              </FormField>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={addLine9Row}>
                {editingLine9Id != null ? "Update vehicle" : "+ Add vehicle"}
              </Button>
              {editingLine9Id != null && (
                <Button variant="ghost" onClick={cancelLine9Edit}>
                  Cancel
                </Button>
              )}
            </div>
          </Panel>

          {line9Rows.length > 0 && (
            <BulkDeleteBar
              count={line9Selection.count}
              onDelete={() => setBulk9ConfirmOpen(true)}
            />
          )}

          <div className="overflow-x-auto">
          <table className="w-full border-collapse mb-1" style={{ tableLayout: "fixed" }}>
            <colgroup>
              {line9Rows.length > 0 && <col style={{ width: 36 }} />}
              <col style={{ width: 40 }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "38%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: 72 }} />
            </colgroup>
            <thead>
              <tr>
                {line9Rows.length > 0 && (
                  <th className="text-left p-3 border-b border-[var(--line)]">
                    <TriStateCheckbox
                      checked={line9Rows.length > 0 && line9Selection.count === line9Rows.length}
                      indeterminate={line9Selection.count > 0 && line9Selection.count < line9Rows.length}
                      onChange={() =>
                        line9Selection.selectAll(
                          line9Selection.count === line9Rows.length ? [] : line9Rows.map((r) => r.id)
                        )
                      }
                      label="Select all sold/transferred vehicles"
                    />
                  </th>
                )}
                {["#", "VIN", "Buyer", "Date", "Actions"].map((h, hi, arr) => (
                  <th
                    key={h}
                    className={[
                      "p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold",
                      hi === arr.length - 1 ? "text-right" : "text-left",
                    ].join(" ")}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {line9Rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-[var(--muted)] p-[16px_12px] text-sm">
                    No vehicles yet. Add your first one above.
                  </td>
                </tr>
              ) : (
                line9Rows.map((row, i) => (
                  <tr key={row.id} className="hover:bg-[var(--accent-soft)] transition-colors">
                    <td className="text-left p-3 border-b border-[var(--line)]">
                      <TriStateCheckbox
                        checked={line9Selection.selectedIds.has(row.id)}
                        onChange={() => line9Selection.toggle(row.id)}
                        label={`Select row ${i + 1}`}
                      />
                    </td>
                    <td className="text-left p-3 border-b border-[var(--line)]">{i + 1}</td>
                    <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px] break-words">{row.vin}</td>
                    <td className="text-left p-3 border-b border-[var(--line)] break-words">{row.buyer}</td>
                    <td className="text-left p-3 border-b border-[var(--line)]">{row.date}</td>
                    <td className="text-right p-3 border-b border-[var(--line)] whitespace-nowrap">
                      <button
                        type="button"
                        title="Edit"
                        onClick={() => startEditLine9(row)}
                        className="inline-flex items-center justify-center border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 hover:text-[var(--accent)]"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        title="Delete"
                        onClick={() => setPendingLine9RemoveId(row.id)}
                        className="inline-flex items-center justify-center border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 ml-1 hover:text-[var(--danger)]"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>

          <ConfirmModal
            open={pendingLine9RemoveId != null}
            title="Delete Confirmation"
            confirmLabel="Delete"
            onConfirm={confirmRemoveLine9}
            onCancel={() => setPendingLine9RemoveId(null)}
          >
            Are you sure you want to delete this record? This action cannot be undone.
          </ConfirmModal>

          <ConfirmModal
            open={bulk9ConfirmOpen}
            title="Delete Selected Records?"
            confirmLabel="Delete"
            onConfirm={confirmBulk9Delete}
            onCancel={() => setBulk9ConfirmOpen(false)}
          >
            Are you sure you want to delete these {line9Selection.count} record{line9Selection.count === 1 ? "" : "s"}?
            This action cannot be undone.
          </ConfirmModal>
        </div>
      )}

      <div className="flex justify-between gap-3 mt-6">
        <Button variant="ghost" onClick={onBack} disabled={saving}>
          ← Back
        </Button>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={handleContinue} disabled={saving || loadingSaved}>
            Skip this step →
          </Button>
          <Button onClick={handleContinue} disabled={saving || loadingSaved}>
            {saving ? "Saving…" : "Continue to credits →"}
          </Button>
        </div>
      </div>
    </Panel>
  );
};

export default PriorYearStep;
